// API Error types for better type safety

export interface ApiErrorResponse {
  message: string
  status?: number
  code?: string
  details?: Record<string, unknown>
}

export interface ValidationError {
  field: string
  message: string
}

export interface ApiError extends Error {
  status?: number
  code?: string
  response?: {
    data?: {
      message?: string
      errors?: ValidationError[]
    }
    status?: number
  }
}

// Network error type
export interface NetworkError extends Error {
  code?: string
  request?: unknown
}

// Authentication specific error types
export interface AuthError extends ApiError {
  type: 'AUTH_ERROR'
  reason?: 'INVALID_CREDENTIALS' | 'TOKEN_EXPIRED' | 'UNAUTHORIZED' | 'FORBIDDEN'
}

// Workspace specific error types
export interface WorkspaceError extends ApiError {
  type: 'WORKSPACE_ERROR'
  reason?: 'NOT_FOUND' | 'ACCESS_DENIED' | 'INVALID_WORKSPACE'
}

// Union type for all possible errors
export type AppError = ApiError | NetworkError | AuthError | WorkspaceError | Error

// Type guard functions
export const isApiError = (error: unknown): error is ApiError => {
  return error instanceof Error && 'status' in error
}

export const isNetworkError = (error: unknown): error is NetworkError => {
  return error instanceof Error && 'code' in error && !('status' in error)
}

export const isAuthError = (error: unknown): error is AuthError => {
  return isApiError(error) && 'type' in error && error.type === 'AUTH_ERROR'
}

export const isWorkspaceError = (error: unknown): error is WorkspaceError => {
  return isApiError(error) && 'type' in error && error.type === 'WORKSPACE_ERROR'
}

// Error message extractor with type safety
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }

  if (isApiError(error)) {
    return error.response?.data?.message || error.message || '서버 오류가 발생했습니다.'
  }

  if (typeof error === 'string') {
    return error
  }

  return '알 수 없는 오류가 발생했습니다.'
}

// HTTP status code error mapping
export const getStatusCodeMessage = (status: number): string => {
  switch (status) {
    case 400:
      return '잘못된 요청입니다.'
    case 401:
      return '인증이 필요합니다.'
    case 403:
      return '권한이 없습니다.'
    case 404:
      return '요청한 리소스를 찾을 수 없습니다.'
    case 409:
      return '데이터 충돌이 발생했습니다.'
    case 422:
      return '입력 데이터가 올바르지 않습니다.'
    case 429:
      return '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.'
    case 500:
      return '서버 오류가 발생했습니다.'
    case 502:
      return '서버에 연결할 수 없습니다.'
    case 503:
      return '서비스를 사용할 수 없습니다.'
    case 504:
      return '서버 응답 시간이 초과되었습니다.'
    default:
      return '알 수 없는 오류가 발생했습니다.'
  }
}